import React from "react";
import { useLocation } from "react-router-dom";

import { useWhiteLabel } from "../../context/WhiteLabel";
import { useThemeContext } from "../../context/DarkMode";
import { SOMBRAS, hexParaHsl } from "../../helpers/estiloVisual";

// Cor por cartão, painel, seção e faixa das páginas. Mora no estilo visual
// (`componentes`), escolhido pelo super admin clicando na própria página
// (PainelDeAparencia) — a empresa leva junto ao escolher o estilo.
//
// A chave é "<pagina>|<item>": a página sai da rota (número vira "id", para a
// filial 12 e a 13 dividirem as cores) e o item, do título do cartão.
// ponytail: título renomeado perde a cor escolhida; um `aparencia="..."`
// explícito no componente fixa a chave quando isso importar.

// O que cada tipo deixa mudar, na ordem do painel.
export const PROPRIEDADES = {
	fundo: "Fundo",
	borda: "Borda",
	texto: "Texto",
	destaque: "Cor de destaque",
	cabecalho: "Cabeçalho",
};

// `caixa`: o que o inspetor deixa mudar da forma (layout, vale nos dois modos).
const CAIXA = ["borda", "estiloborda", "canto", "padding", "sombra"];

export const TIPOS = {
	cartao: { nome: "Cartão", plural: "cartões", props: ["fundo", "borda", "texto", "destaque"], caixa: CAIXA },
	painel: { nome: "Painel", plural: "painéis", props: ["fundo", "cabecalho", "borda", "texto", "destaque"], caixa: CAIXA },
	// Cartão montado à mão, sem tile de ícone para o destaque.
	bloco: { nome: "Cartão", plural: "cartões", props: ["fundo", "borda", "texto"], caixa: CAIXA },
	// A seção é moldura de box-shadow (Bloco): sem tracejado nem sombra.
	secao: { nome: "Seção", plural: "seções", props: ["fundo", "borda"], caixa: ["borda", "canto", "padding"] },
	faixa: { nome: "Faixa do topo", plural: "faixas", props: ["fundo", "destaque", "texto"], caixa: ["borda", "estiloborda", "canto", "sombra"] },
};

export const ESTILOS_DE_BORDA = { solida: "solid", tracejada: "dashed", nenhuma: "none" };

// As chaves de `layout` que são forma, não lugar (ordem e largura são lugar).
export const CHAVES_DE_CAIXA = CAIXA;

export const slug = (texto) =>
	String(texto || "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 60);

export const paginaDaRota = (pathname) =>
	pathname
		.split("/")
		.filter(Boolean)
		.map((parte) => (/^\d+$/.test(parte) ? "id" : slug(parte)))
		.join(".") || "inicio";

// Texto da cor escolhida: os `text-foreground`/`text-muted-foreground` lá
// dentro leem estas variáveis no próprio elemento.
const textoEm = (cor) => ({
	color: cor,
	"--color-foreground": cor,
	"--color-card-foreground": cor,
	"--color-muted-foreground": `color-mix(in srgb, ${cor} 72%, transparent)`,
});

// Fundo e cor de ícone sobre o destaque, no lugar do `bg-sky-500/10 text-sky-600`.
export const tileDe = (cor) =>
	cor ? { color: cor, backgroundColor: `color-mix(in srgb, ${cor} 14%, transparent)` } : undefined;

const NADA = { attrs: {}, cores: {}, layout: {} };

// `nome` é o que o painel de edição mostra; `id` cai no slug do nome.
export const useAparencia = ({ id, nome, tipo = "cartao" }) => {
	const { pathname } = useLocation();
	const { estilo, rascunho, previa, selecionado, setSelecionado } = useWhiteLabel();
	const { darkMode } = useThemeContext();

	const item = id || slug(nome);
	if (!item) return NADA;

	const chave = `${paginaDaRota(pathname)}|${item}`;
	const cores = estilo?.componentes?.[chave]?.[darkMode ? "escuro" : "claro"] || {};

	const style = {};
	if (cores.fundo) style.backgroundColor = cores.fundo;
	if (cores.borda) style.borderColor = cores.borda;
	if (cores.texto) Object.assign(style, textoEm(cores.texto));
	// A faixa do topo pinta o degradê com `--primary` (globals.css, .faixa-hero).
	if (tipo === "faixa" && cores.destaque) {
		const hsl = hexParaHsl(cores.destaque);
		if (hsl) style["--primary"] = hsl;
	}

	// Posição e largura (components/Aparencia/Painel): `order` e colunas da
	// grade de quem contém o bloco. A largura só vale do xl para cima
	// (globals.css) — no celular continua um embaixo do outro.
	const layout = estilo?.componentes?.[chave]?.layout || {};
	if (layout.ordem !== undefined) style.order = Number(layout.ordem);

	// Forma (inspetor › Item): borda, canto, espaçamento interno e sombra. O
	// espaçamento vai em `--pad`, que Panel/KpiCard leem no lugar do
	// `p-5` (o cabeçalho sangrado acompanha); o cartão montado à mão recebe o
	// padding direto. Sombra inline vence a do tema e o hover do `.cartao-vivo`.
	if (layout.borda !== undefined) style.borderWidth = `${Number(layout.borda)}px`;
	if (ESTILOS_DE_BORDA[layout.estiloborda]) style.borderStyle = ESTILOS_DE_BORDA[layout.estiloborda];
	if (layout.canto !== undefined) style.borderRadius = `${Number(layout.canto)}px`;
	// Sem escolha, `initial` zera o `--pad` herdado de quem contém (o cartão
	// dentro de um painel de 32px não leva os 32px) e o `var()` cai no padrão.
	style["--pad"] = layout.padding !== undefined ? `${Number(layout.padding)}px` : "initial";
	if (tipo === "bloco" && layout.padding !== undefined) style.padding = style["--pad"];
	const sombra = SOMBRAS.find((s) => s.id && s.id === layout.sombra);
	if (sombra) style.boxShadow = sombra[darkMode ? "escuro" : "claro"];

	const attrs = {
		style,
		"data-aparencia": chave,
		"data-aparencia-nome": nome || item,
		"data-aparencia-tipo": tipo,
	};
	if (layout.largura) {
		attrs["data-largura"] = layout.largura;
		if (layout.largura !== "inteira") style["--largura"] = Number(layout.largura);
	}
	if (rascunho && !previa) {
		if (selecionado?.chave === chave) attrs["data-aparencia-selecionado"] = "";
		// Os filhos não recebem clique em modo de edição (globals.css): o
		// alvo é sempre o item mais de dentro, e a ação dele não dispara.
		attrs.onClick = (e) => {
			e.preventDefault();
			e.stopPropagation();
			setSelecionado({ chave, nome: nome || item, tipo });
		};
	}

	return { attrs, cores, layout };
};

// Funde o `style` de quem chama com o da aparência (a aparência vence).
export const comEstilo = (attrs, style) =>
	style ? { ...attrs, style: { ...style, ...attrs.style } } : attrs;

// Bloco qualquer da página que ganha cor: seção inteira, faixa, cartão solto.
// Seção sem cor fica exatamente como era. Com cor, a moldura é sombra sem
// desfoque: pinta 12px para fora sem mexer em margem nem no lugar de nada
// (margem negativa brigaria com o `space-y` de quem a contém).
const MOLDURA = 12;

export const Bloco = ({ as: Tag = "div", aparencia, nome, tipo = "secao", className, style, children, ...resto }) => {
	const { attrs, cores, layout } = useAparencia({ id: aparencia, nome, tipo });
	let final = comEstilo(attrs, style);
	// Sem cor nem espessura escolhida, a seção não ganha anel de borda.
	const larguraDaBorda = layout.borda !== undefined ? Number(layout.borda) : cores.borda ? 1 : 0;
	if (tipo === "secao" && (cores.fundo || cores.borda || layout.borda !== undefined)) {
		// Seção não tem borda própria: a borda vai na moldura, e o espaçamento
		// interno escolhido é a espessura dela.
		const moldura = layout.padding !== undefined ? Number(layout.padding) : MOLDURA;
		const semBorda = { ...final.style };
		["borderColor", "borderWidth", "borderStyle", "borderRadius", "boxShadow"].forEach((k) => delete semBorda[k]);
		final = {
			...final,
			style: {
				...semBorda,
				borderRadius: layout.canto !== undefined ? Number(layout.canto) : MOLDURA,
				boxShadow: [
					`0 0 0 ${moldura}px ${cores.fundo || "transparent"}`,
					larguraDaBorda ? `0 0 0 ${moldura + larguraDaBorda}px ${cores.borda || "hsl(var(--border))"}` : null,
				]
					.filter(Boolean)
					.join(", "),
			},
		};
	}
	return (
		<Tag {...resto} {...final} className={className}>
			{children}
		</Tag>
	);
};
